import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { fetchPublicSettings } from '@/services/settings'
import type { StoreSettings } from '@/types'

interface StoreGateContextValue {
  status: 'loading' | 'ready'
  settings: StoreSettings | null
  refresh: () => Promise<void>
}

const StoreGateContext = createContext<StoreGateContextValue | null>(null)

export function StoreGateProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<StoreSettings | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready'>('loading')

  const load = async () => {
    try {
      const data = await fetchPublicSettings()
      setSettings(data)
    } catch {
      setSettings(null)
    } finally {
      setStatus('ready')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const value = useMemo<StoreGateContextValue>(
    () => ({
      status,
      settings,
      refresh: load,
    }),
    [settings, status]
  )

  return (
    <StoreGateContext.Provider value={value}>{children}</StoreGateContext.Provider>
  )
}

export function useStoreGate() {
  const ctx = useContext(StoreGateContext)
  if (!ctx) {
    throw new Error('useStoreGate must be used within StoreGateProvider')
  }
  return ctx
}
